import React, { useState, useEffect } from "react";
import { apiPost, apiGet } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";
import { useForm } from "react-hook-form";
import useToastContext from "hooks/useToastContext";
import OInputField from "components/reusable/OInputField";
import { useTranslation } from "react-i18next";
import { preventMaxInput } from "utils/validations";
import formValidation from "../../utils/formValidation";
import dayjs from "dayjs";
import ErrorMessage from "components/ErrorMessage";
import FormDateRangePicker from "components/shared/FormDateRangePicker";
import fileImage from "../../assets/images/file_Image.png";
import photoClose from "../../assets/images/photo_close.png";
import { LuUpload } from "react-icons/lu";
import helpers from "utils/helpers";
import { Editor } from "@tinymce/tinymce-react";

const EmployeeTaskAdd = ({ allTaskScheduling, handleCategory, item }) => {
  const { t } = useTranslation();
  const notification = useToastContext();

  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    reset,
    watch,
    setValue,
    getValues, // ═══ ADDED ═══
    formState: { errors },
  } = useForm({
    mode: "onChange",
    shouldFocusError: true,
    defaultValues: {
      taskType: "standalone",
      name: "",
      address: ""
    },
  });

  const [recordsTemp, setRecordTemp] = useState([]);
  const [cat, setCat] = useState("");
  const [timeZoneTemp, setTimeZoneTemp] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [timeZones, setTimeZones] = useState("");
  const [selectStartDate, setSelectStartDate] = useState();
  const [selectEndDate, setSelectEndDate] = useState();
  const [dateErrors, setDateErrors] = useState({
    startDate: "",
    endDate: "",
  });

  // ═══ Shift-related state ═══
  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState("");
  const [selectedShiftData, setSelectedShiftData] = useState(null);
  const [taskType, setTaskType] = useState("standalone");

  const [fileError, setFileError] = useState("");
  const [images, setImages] = useState([]);
  const [description, setDescription] = useState("");

  const handleImageChangeData = (e) => {
    const selectedFiles = e.target.files;
    const areAllImagesData = Array.from(selectedFiles).every((file) => 
      ["pdf", "txt", "docx"].includes(file.name.split(".")[file.name.split(".").length - 1])
    );

    if (areAllImagesData) {
      const newImages = Array.from(selectedFiles).slice(0, 5);
      const fileSize = (e.target.files[0]?.size / (1024 * 1024)).toFixed(2);
      if (fileSize > 15) {
        notification.error("Please select file below 15Mb.");
        return false;
      }
      if (images.length + newImages.length <= 5) {
        setImages((prevImages) => [...prevImages, ...newImages]);
      } else {
        notification.error(`You can upload up to 5 File.`);
      }
      setFileError(false);
    } else {
      notification.error(`Please select only pdf,txt and docs files.`);
    }
  };

  const checkFields = () => {
    let isValidD = true;
    if (images?.length) {
      setFileError(false);
    } else {
      setFileError(true);
      isValidD = false;
    }
    return isValidD;
  };

  const handleRemoveImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
  };

  const validateFuncData = () => {
    let isValid = true;
    
    // ═══ Validate Task Name (required for both types) ═══
    const taskName = getValues("name");
    if (!taskName || taskName.trim() === "") {
      setError("name", {
        type: "manual",
        message: t("TASK_NAME_REQUIRED")
      });
      isValid = false;
    } else {
      clearErrors("name");
    }
    
    // ═══ Validate Address (required for both types) ═══
    const address = getValues("address");
    if (!address || address.trim() === "") {
      setError("address", {
        type: "manual",
        message: t("TASK_ADDRESS_REQUIRED")
      });
      isValid = false;
    } else {
      clearErrors("address");
    }
    
    // For shift-linked tasks, we don't need date validation
    if (taskType === 'shift') {
      setDateErrors({});
      return isValid;
    }

    // For standalone tasks, validate dates
    if (!selectStartDate && !selectEndDate) {
      setDateErrors({
        startDate: t("PLEASE_SELECT_START_DATE"),
        endDate: t("PLEASE_SELECT_END_DATE"),
      });
      return false;
    }
    if (!selectStartDate) {
      setDateErrors({
        startDate: t("PLEASE_SELECT_START_DATE"),
      });
      return false;
    } else {
      setDateErrors({
        ...dateErrors,
        startDate: "",
      });
    }
    if (!selectEndDate) {
      setDateErrors({
        endDate: t("PLEASE_SELECT_END_DATE"),
      });
      return false;
    } else {
      setDateErrors({
        ...dateErrors,
        endDate: "",
      });
    }
    setDateErrors({});
    return isValid;
  };

  useEffect(() => {
    if (dateErrors.startDate || dateErrors.endDate) {
      validateFuncData();
    }
  }, [selectStartDate, selectEndDate]);

  const onSubmit = async (data) => {
    try {
      // ═══ Manually validate before submit ═══
      const isValid = validateFuncData();
      if (!isValid) {
        return;
      }
      
      setIsLoading(true);
      const isValidD = checkFields();
      if (!isValidD) return;

      const formData = new FormData();

      const appendFormData = (key, value) => helpers.andCondition(value, formData.append(key, value));

      appendFormData("address", data.address);
      appendFormData("name", data.name);
      appendFormData("description", description);
      appendFormData("employeeId", cat);
      appendFormData("timeZone", timeZones);
      appendFormData("taskType", taskType);

      // ═══ Add shiftId if shift-linked ═══
      if (taskType === 'shift' && selectedShift) {
        appendFormData("shiftId", selectedShift);
        // ═══ Also send dates from shift ═══
        if (selectedShiftData) {
          appendFormData("startDateTime", selectedShiftData.startDateTime);
          appendFormData("endDateTime", selectedShiftData.endDateTime);
        }
      }

      // For standalone tasks, add dates
      if (taskType === 'standalone') {
        if (selectStartDate !== "") {
          formData.append("startDateTime", selectStartDate);
        }
        if (selectEndDate !== "") {
          formData.append("endDateTime", selectEndDate);
        }
      }

      if (images?.length > 0) {
        images?.map((items, index) => {
          formData.append("documents", items);
        });
      }

      // Uncomment this when ready
      const res = await apiPost(apiPath.taskAdd, formData);

      function successCallback() {
        allTaskScheduling();
        handleCategory(false);
        notification.success(res?.data?.message);
      }
      function errorCallback() {
        notification.error(res?.data?.message);
      }
      helpers.handleResult(res?.data?.success, successCallback, errorCallback);
    } catch (err) {
      console.log("err:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (start, end) => {
    setSelectStartDate(dayjs(start).toDate());
    setSelectEndDate(dayjs(end).toDate());
  };

  const getTimeZoneList = async () => {
    try {
      const res = await apiGet(apiPath.getTimeZoneList, {});
      setTimeZoneTemp(res?.data?.results);
    } catch (error) {
      console.log("error:", error);
    }
  };

  const getEmployeeList = async () => {
    try {
      const res = await apiGet(apiPath.getEmployeeListTask, {});
      setRecordTemp(res?.data?.results);
    } catch (error) {
      console.log("error:", error);
    }
  };

  // ═══ Get shifts for shift-linked tasks ═══
 

const getShifts = async () => {
  try {
    console.log("Selected employee ID:", cat);

    const res = await apiGet(apiPath.getShiftForTask, {
      status: "assigned",
      employeeId: cat,
    });

    console.log("Full shift response:", res);
    console.log("Response data:", res?.data);

    // Check common response formats
    const shiftList =
      Array.isArray(res?.data?.results) ? res.data.results :
      Array.isArray(res?.data?.data) ? res.data.data :
      Array.isArray(res?.data) ? res.data :
      Array.isArray(res?.results) ? res.results :
      [];

    console.log("Extracted shifts:", shiftList);

    setShifts(shiftList);
  } catch (error) {
    console.error(
      "Shift API error:",
      error.response?.data || error.message
    );
    setShifts([]);
  }
};

  useEffect(() => {
    getEmployeeList();
    getTimeZoneList();
  }, []);

  // ═══ Fetch shifts when employee changes ═══
 
useEffect(() => {
  if (taskType === "shift" && cat) {
    setShifts([]);
    setSelectedShift("");
    setSelectedShiftData(null);
    getShifts();
  } else {
    setShifts([]);
    setSelectedShift("");
    setSelectedShiftData(null);
  }
}, [cat, taskType]);

  // ═══ Update date fields and address when shift is selected ═══
  useEffect(() => {
    if (taskType === 'shift' && selectedShift) {
      // Find the selected shift data from shifts array
      const shiftData = shifts.find(s => s._id === selectedShift);
      if (shiftData) {
        setSelectedShiftData(shiftData);
        
        // Set the date fields with shift's start and end datetime
        setSelectStartDate(shiftData.startDateTime);
        setSelectEndDate(shiftData.endDateTime);
        setDateErrors({});
        
        // ═══ ═══ ═══ AUTO-FILL ADDRESS FROM SHIFT LOCATION ═══ ═══ ═══
        if (shiftData.locationId?.address) {
          setValue("address", shiftData.locationId.address);
          clearErrors("address");
        }
        
        // Also set the timezone from shift location if available
        if (shiftData.locationId?.timezone) {
          setTimeZones(shiftData.locationId.timezone);
        }
      }
    } else {
      // Clear shift data when task type changes or no shift selected
      setSelectedShiftData(null);
    }
  }, [selectedShift, shifts, taskType, setValue, clearErrors]);

  // ═══ Display shift dates in UI ═══
  const renderShiftDates = () => {
    if (taskType === 'shift' && selectedShiftData) {
      return (
        <div className="grid sm:grid-cols-2">
          <div className="p-2">
            <div className="relative z-0 w-full group">
              <OInputField
                disabled
                inputLabel={
                  <>
                    {t("SHIFT_START_DATE_TIME")}
                  </>
                }
                wrapperClassName="w-full group relative z-0"
                type="text"
                name="shiftStartDateTime"
                value={dayjs(selectedShiftData.startDateTime).format("MM-DD-YYYY HH:mm")}
                register={register("shiftStartDateTime")}
                errors={errors}
              />
            </div>
          </div>
          <div className="p-2">
            <div className="relative z-0 w-full group">
              <OInputField
                disabled
                inputLabel={
                  <>
                    {t("SHIFT_END_DATE_TIME")}
                  </>
                }
                wrapperClassName="w-full group relative z-0"
                type="text"
                name="shiftEndDateTime"
                value={dayjs(selectedShiftData.endDateTime).format("MM-DD-YYYY HH:mm")}
                register={register("shiftEndDateTime")}
                errors={errors}
              />
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const filteredArray = recordsTemp && recordsTemp?.filter((i) => i?._id == cat)?.map((data) => data?.employeeId);

  useEffect(() => {
    reset({
      employeeId: filteredArray,
    });
  }, [recordsTemp, reset, cat]);

  const selectCategory = (e) => {
    if (e.target.value) {
      setCat(e.target.value);
      clearErrors("_id");
    } else {
      setCat("");
      setError("_id", {
        type: "validation",
        message: "Please select employee.",
      });
    }
  };

  const selectTimeZone = (e) => {
    if (e.target.value) {
      setTimeZones(e.target.value);
    } else {
      setTimeZones("");
    }
  };

  // ═══ Handle task type change ═══
  const handleTaskTypeChange = (e) => {
    const newType = e.target.value;
    setTaskType(newType);
    if (newType === 'shift') {
      // Clear date fields for shift tasks
      setSelectStartDate(null);
      setSelectEndDate(null);
      setDateErrors({});
      setSelectedShift("");
      setSelectedShiftData(null);
    } else {
      // Clear address when switching to standalone
      setValue("address", "");
    }
  };

  // ═══ Handle shift selection ═══
  const handleShiftChange = (e) => {
    const shiftId = e.target.value;
    setSelectedShift(shiftId);
  };

  return (
    <>
      <div className="fixed inset-0 z-50 justify-center items-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none">
        <div className="mx-auto relative w-auto my-6 max-w-3xl">
          <div className="sm:px-2 md:py-4 sm:py-8 px-7">
            <form
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="focus:outline-none border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none min-w-[762px]">
                <div className="p-5 flex items-center justify-between border-b border-solid border-slate-200 rounded-t">
                  <h3 className="font-semibold text-xl ">{t("EMPLOYEE_TASK_ASSIGN")}</h3>
                  <button
                    onClick={() => handleCategory(false)}
                    className=" h-8 w-8 ml-auto flex items-center justify-center  text-black border-2 rounded-full  float-right text-3xl leading-none font-extralight outline-none focus:outline-none"
                    title="Close"
                  >
                    <span className=" text-[#B8BBBF]  text-4xl " title="Close">
                      ×
                    </span>
                  </button>
                </div>

                <div className="relative p-6 flex-auto">
                  {/* Employee Selection */}
                  <div className="grid sm:grid-cols-2">
                    <div className="p-2">
                      <div className="relative z-0  w-full group">
                        <select
                          placeholder=" "
                          value={cat}
                          {...register("_id", {
                            required: "Please select employee.",
                          })}
                          onChange={selectCategory}
                          type="select"
                          id="_id"
                          className="dark:border-gray-600 dark:focus:border-blue-500 block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none dark:text-white focus:outline-none focus:ring-0  peer"
                        >
                          <option key="" value="">
                            {t("SELECT_EMPLOYEE")}
                          </option>
                          {recordsTemp?.map((categoryItem) => {
                            return <option key={categoryItem?._id} value={categoryItem?._id}>
                              {categoryItem?.name} ({categoryItem?.employeeId})
                            </option>;
                          })}
                        </select>
                        <label
                          className="duration-300 peer-focus:font-normal absolute text-sm text-[#A5A5A5] dark:text-gray-400 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
                          htmlFor="_id"
                        >
                          {" "}
                          {t("SELECT_EMPLOYEE")}
                          <span className="text-red-500">*</span>
                        </label>
                        <ErrorMessage message={errors?._id?.message} />
                      </div>
                    </div>
                    <div className="p-2">
                      <div className="relative z-0 w-full group">
                        <OInputField
                          disabled
                          inputLabel={
                            <>
                              {t("EMPLOYEE_ID")}
                              <span className="text-red-500">*</span>
                            </>
                          }
                          wrapperClassName="w-full group relative z-0 "
                          type="text"
                          name="employeeId"
                          onInput={(e) => preventMaxInput(e, 50)}
                          register={register("employeeId", formValidation["employeeID"])}
                          errors={errors}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Task Type and Shift Selection */}
                  <div className="grid sm:grid-cols-2">
                    <div className="p-2">
                      <div className="relative z-0 w-full group">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("TASK_TYPE")}
                          <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={taskType}
                          onChange={handleTaskTypeChange}
                          className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
                        >
                          <option key="standalone" value="standalone">
                            {t("STANDALONE_TASK")}
                          </option>
                          <option key="shift" value="shift">
                            {t("SHIFT_LINKED_TASK")}
                          </option>
                        </select>
                      </div>
                    </div>
                    {taskType === 'shift' && (
                      <div className="p-2">
                        <div className="relative z-0 w-full group">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("SELECT_SHIFT")}
                            <span className="text-red-500">*</span>
                          </label>
                         
<select
  value={selectedShift}
  onChange={handleShiftChange}
  disabled={!cat}
  className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF]"
>
  <option value="">
    {!cat
      ? "Select an employee first"
      : shifts.length === 0
      ? "No shifts available"
      : t("SELECT_SHIFT")}
  </option>

  {shifts.map((shift) => (
    <option key={shift._id} value={shift._id}>
      {shift.name || "Unnamed Shift"} (
      {dayjs(shift.startDateTime).format("MM-DD HH:mm")} -{" "}
      {dayjs(shift.endDateTime).format("HH:mm")})
    </option>
  ))}
</select>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Show Shift Dates */}
                  {renderShiftDates()}

                  {/* Task Name */}
                  <div className="grid sm:grid-cols-2">
                    <div className="p-2">
                      <div className="relative z-0 w-full group">
                        <OInputField
                          inputLabel={
                            <>
                              {t("TASK_NAME")}
                              <span className="text-red-500">*</span>
                            </>
                          }
                          wrapperClassName="relative z-0 w-full group"
                          type="text"
                          name="name"
                          maxLength={500}
                          onInput={(e) => preventMaxInput(e, 500)}
                          register={register("name", {
                            required: t("TASK_NAME_REQUIRED"),
                            validate: (value) => value && value.trim() !== "" || t("TASK_NAME_REQUIRED")
                          })}
                          errors={errors}
                        />
                      </div>
                    </div>
                    <div className="p-2">
                      <div className="relative z-0  w-full group">
                        <select
                          type="select"
                          id="timeZoneID"
                          className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg dark:text-white dark:border-gray-600 border-[#DFDFDF] appearance-none dark:focus:border-blue-500 focus:outline-none focus:ring-0  peer"
                          value={timeZones}
                          {...register("timeZoneID")}
                          placeholder=" "
                          onChange={selectTimeZone}
                        >
                          <option key="" value="">{t("SELECT_TIME_ZONE")}</option>
                          {timeZoneTemp?.map((timezoneItem) => {
                            return (
                              <option key={timezoneItem?._id} value={timezoneItem?._id}>
                                {timezoneItem?.name} ({timezoneItem?.timezone})
                              </option>
                            );
                          })}
                        </select>
                        <label
                          htmlFor="timeZoneID"
                          className="dark:text-gray-400 duration-300 peer-focus:font-normal absolute text-sm text-[#A5A5A5] transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
                        >
                          {" "}
                          {t("SELECT_TIME_ZONE")}
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Date Picker (Only for standalone tasks) */}
                  {taskType === 'standalone' && (
                    <div className="p-2">
                      <FormDateRangePicker 
                        selectDateFrom 
                        selectDateTo 
                        handleDateChange={handleDateChange} 
                        selectStartDate={selectStartDate} 
                        addFlex={false} 
                        errors={dateErrors} 
                        selectEndDate={selectEndDate} 
                      />
                    </div>
                  )}

                  {/* Address */}
                  <div className="grid sm:grid-cols-1">
                    <div className="px-2 pb-4">
                      <div className="relative z-0 w-full group">
                        <OInputField
                          name="address"
                          inputLabel={
                            <>
                              {t("TASK_ADDRESS")}
                              <span className="text-red-500">*</span>
                            </>
                          }
                          wrapperClassName="relative z-0 w-full group"
                          type="text"
                          maxLength={500}
                          onInput={(e) => preventMaxInput(e, 500)}
                          register={register("address", {
                            required: t("TASK_ADDRESS_REQUIRED"),
                            validate: (value) => value && value.trim() !== "" || t("TASK_ADDRESS_REQUIRED")
                          })}
                          errors={errors}
                        />
                      </div>
                    </div>
                    
                    {/* File Upload */}
                    <div className="mt-3">
                      <div className="relative z-0  w-full group">
                        <label
                          className="mb-3 peer-focus:font-normal absolute text-lg text-[#A5A5A5] dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-[-10px] left-[-10px] bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
                          htmlFor="uploadFile"
                        >
                          {" "}
                          {t("UPLOAD_FILE")}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="relative block py-3 h-14 px-3 w-full text-sm text-gray-900 bg-transparent  appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0  peer">
                          <input type="file" className="form-control opacity-0 absolute top-0 bottom-0 cursor-pointer z-50" accept="application/pdf, text/plain, application/doc" multiple={true} onChange={handleImageChangeData} />

                          <div className="flex items-center absolute top-0  cursor-pointer">
                            <figure className="p-4 border rounded-lg mr-3">
                              <LuUpload />
                            </figure>
                            <figcaption>Choose a file to upload</figcaption>
                          </div>
                        </div>

                        <div className="grid grid-cols-3">
                          {images?.map((image, index) => (
                            <div className="p-2 w-25 " key={index}>
                              <div className="border-2 p-3 rounded-lg relative report_img_h w-100 h-100 rounded-3 report_imag_resize">
                                <button variant="link" className="text-decoration-none absolute right-[-10px] top-[-13px] me-1 mt-1 bg-danger rounded-circle p-0 " onClick={() => handleRemoveImage(index)}>
                                  <img src={photoClose} width={24} alt="" height={24} />
                                </button>

                                <div className="flex items-center">
                                  <span className="block mr-3">
                                    <img src={fileImage} alt={`Uploaded Image ${index + 1}`} className="w-100 h-100 object-fit-cover " />
                                  </span>
                                  <span className="truncate block max-w-[160px]">{image?.name}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        {fileError && <ErrorMessage message="Please upload file." />}
                      </div>
                    </div>

                    {/* Description */}
                    <div className="p-2">
                      <div className="relative z-0 w-full group">
                        <label className='peer-focus:font-normal absolute text-sm text-[#A5A5A5] dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8'>
                          {t('DESCRIPTION')}
                        </label>
                        <Editor
                          apiKey={process.env.REACT_APP_TINY_MCE_KEY}
                          value={description}
                          onEditorChange={(content) => {
                            setDescription(content);
                          }}
                          init={{
                            plugins: ["lists", "autolink link image charmap preview anchor", "searchreplace visualblocks code fullscreen", "insertdatetime media table paste help wordcount", "table"],
                            toolbar: "undo redo | formatselect | bold italic underline strikethrough blockquote | alignleft aligncenter alignright alignjustify | bullist numlist",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b">
                  <button className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6  ease-linear transition-all duration-150" type="button" onClick={() => handleCategory(false)}>
                    {t("O_BACK")}
                  </button>
                  {helpers.ternaryCondition(
                    isLoading,
                    <div className="spinner-container bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1  ease-linear transition-all duration-150">
                      <div className="loading-spinner"></div>
                    </div>,
                    <button className="bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1  ease-linear transition-all duration-150">{t("O_ADD")}</button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black" />
    </>
  );
};

export default EmployeeTaskAdd;